import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { getImageUrl, getSubscriptionPlans, upgradeSubscription } from '../services/api';
import './Dashboard.css';

const roleLabels = {
  photographer: 'Photographe',
  user: 'Utilisateur',
};

const Dashboard = () => {
  const { user, setUser, isAuthenticated, loading: authLoading } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchDashboardData();
    }
  }, [isAuthenticated, user]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/analytics/dashboard');
      setDashboardData(response);
    } catch (err) {
      console.error('Dashboard data error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Show loading while auth is initializing
  if (authLoading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Chargement de votre tableau de bord...</p>
      </div>
    );
  }

  // User must be authenticated (ProtectedRoute should handle this)
  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <div className="dashboard">
      {/* Header Section */}
      <div className="dashboard-header">
        <div className="welcome-section">
          <h1>Bienvenue, {user.name} !</h1>
          <p className="subtitle">
            {user.role === 'photographer'
              ? 'Gérez vos événements et importez des photos pour vos clients'
              : 'Découvrez les photos où vous apparaissez grâce à la reconnaissance faciale par IA'}
          </p>
        </div>

        <div className="user-profile-badge">
          <div className="profile-avatar">
            {user.profile_photo ? (
              <img src={ getImageUrl(user.profile_photo) } alt={user.name} />
            ) : (
              <div className="avatar-placeholder">
                {user.name.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="profile-info">
            <span className="profile-name">{user.name}</span>
            <span className="profile-role">{roleLabels[user.role] || user.role}</span>
          </div>
        </div>
      </div>

      {/* Quick Stats Section */}
      {!loading && dashboardData && (
        <div className="dashboard-stats">
          {user.role === 'photographer' ? (
            <PhotographerStats data={dashboardData} />
          ) : (
            <UserStats data={dashboardData} />
          )}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="stats-loading">
          <div className="spinner-small"></div>
          <p>Chargement des statistiques...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="error-banner">
          <span className="error-icon">⚠️</span>
          <p>Impossible de charger les données du tableau de bord : {error}</p>
          <button onClick={fetchDashboardData} className="btn-retry">
            Réessayer
          </button>
        </div>
      )}

      {user.role === 'photographer' && (
        <PhotographerSubscription user={user} setUser={setUser} />
      )}

      {/* Quick Actions Section */}
      <div className="dashboard-actions">
        <h2>Actions rapides</h2>
        <div className="actions-grid">
          {user.role === 'photographer' ? (
            <PhotographerActions navigate={navigate} />
          ) : (
            <UserActions navigate={navigate} user={user} />
          )}
        </div>
      </div>

      {/* Recent Activity Section */}
      <div className="dashboard-recent">
        <h2>Activité récente</h2>
        {user.role === 'photographer' ? (
          <PhotographerActivity />
        ) : (
          <UserActivity />
        )}
      </div>
    </div>
  );
};

// ============================================================================
// PHOTOGRAPHER STATS COMPONENT
// ============================================================================
const PhotographerStats = ({ data }) => {
  return (
    <>
      <StatCard
        icon="📅"
        label="Événements"
        value={data.events || 0}
        color="#FF8C42"
      />
      <StatCard
        icon="📸"
        label="Photos"
        value={(data.photos || 0).toLocaleString()}
        color="#4CAF50"
      />
      <StatCard
        icon="💾"
        label="Stockage serveur"
        value={formatStorage(data.storage_used_bytes, data.storage_used_mb)}
        color="#00ACC1"
      />
      <StatCard
        icon="💰"
        label="Revenus"
        value={`${(data.revenue || 0).toFixed(2)} F`}
        color="#2196F3"
      />
    </>
  );
};

const formatStorage = (bytes, megabytes) => {
  const sizeInBytes = Number.isFinite(Number(bytes))
    ? Number(bytes)
    : Number(megabytes || 0) * 1048576;

  if (sizeInBytes <= 0) return '0 MB';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = sizeInBytes;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const decimals = value >= 10 || unitIndex < 2 ? 0 : 1;
  return `${value.toFixed(decimals)} ${units[unitIndex]}`;
};

// ============================================================================
// PHOTOGRAPHER SUBSCRIPTION
// ============================================================================
const PhotographerSubscription = ({ user, setUser }) => {
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [message, setMessage] = useState(null);
  const currentPlan = user.subscription_plan || 'free';

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await getSubscriptionPlans();
      setPlans(response.plans || {});
    } catch (error) {
      console.error('Subscription plans error:', error);
      setMessage({
        type: 'error',
        text: error.message || "Impossible de charger les forfaits d'abonnement."
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelection = async (planKey) => {
    if (planKey === currentPlan || selectedPlan) return;

    try {
      setSelectedPlan(planKey);
      setMessage(null);
      const response = await upgradeSubscription({ plan: planKey });
      const updatedUser = { ...user, subscription_plan: planKey };

      setUser(updatedUser);
      localStorage.setItem('userData', JSON.stringify(updatedUser));
      setMessage({
        type: 'success',
        text: response.message || response.data?.message || 'Votre forfait a été mis à jour.'
      });
    } catch (error) {
      console.error('Subscription upgrade error:', error);
      setMessage({
        type: 'error',
        text: error.message || error.response?.data?.message || 'Impossible de mettre à jour votre forfait.'
      });
    } finally {
      setSelectedPlan(null);
    }
  };

  const getPlanColor = (planKey) => {
    switch (planKey) {
      case 'basic': return '#4CAF50';
      case 'pro': return '#FF8C42';
      case 'enterprise': return '#9C27B0';
      default: return '#607D8B';
    }
  };

  const getPlanIcon = (planKey) => {
    switch (planKey) {
      case 'basic': return '✓';
      case 'pro': return '★';
      case 'enterprise': return '♛';
      default: return '↗';
    }
  };

  const formatPlanName = (planKey, plan) => {
    if (plan?.name) return plan.name;
    return planKey.charAt(0).toUpperCase() + planKey.slice(1);
  };

  if (loading) {
    return (
      <section className="subscription-section">
        <div className="subscription-header">
          <div>
            <h2>Forfaits photographe</h2>
            <p>Chargement des forfaits disponibles...</p>
          </div>
          <div className="spinner-small"></div>
        </div>
      </section>
    );
  }

  if (!plans || !Object.keys(plans).length) {
    return (
      <section className="subscription-section">
        <div className="subscription-header">
          <div>
            <h2>Forfaits photographe</h2>
            <p>Aucun forfait d'abonnement n'est disponible pour le moment.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="subscription-section">
      <div className="subscription-header">
        <div>
          <h2>Choisissez votre forfait</h2>
          <p>Sélectionnez l'abonnement adapté à votre volume d'événements et de photos.</p>
        </div>
        <div className="current-plan-pill">
          Actuel : {formatPlanName(currentPlan, plans[currentPlan])}
        </div>
      </div>

      {message && (
        <div className={`subscription-message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="subscription-grid">
        {Object.entries(plans).map(([planKey, plan]) => {
          const planColor = getPlanColor(planKey);
          const isCurrentPlan = currentPlan === planKey;
          const isProcessing = selectedPlan === planKey;

          return (
            <div
              key={planKey}
              className={`subscription-card ${isCurrentPlan ? 'current' : ''} ${planKey === 'pro' ? 'featured' : ''}`}
              style={{ borderColor: isCurrentPlan ? planColor : undefined }}
            >
              {planKey === 'pro' && <span className="popular-badge">Populaire</span>}
              {isCurrentPlan && <span className="current-badge" style={{ background: planColor }}>Actuel</span>}

              <div className="plan-heading">
                <span className="plan-icon" style={{ color: planColor }}>{getPlanIcon(planKey)}</span>
                <h3>{formatPlanName(planKey, plan)}</h3>
              </div>

              <div className="plan-price">
                {Number(plan.price || 0).toFixed(2)} F
                <span>/mois</span>
              </div>

              <ul className="plan-features">
                {(plan.features || []).map((feature, index) => (
                  <li key={index}>
                    <span style={{ color: planColor }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                className="plan-select-button"
                onClick={() => handlePlanSelection(planKey)}
                disabled={isCurrentPlan || selectedPlan}
                style={{ background: isCurrentPlan ? '#95A5A6' : planColor }}
              >
                {isCurrentPlan ? 'Forfait actuel' : isProcessing ? 'Mise à jour...' : 'Choisir ce forfait'}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
};

// ============================================================================
// USER STATS COMPONENT
// ============================================================================
const UserStats = ({ data }) => {
  return (
    <>
      <StatCard
        icon="📸"
        label="Photos trouvées"
        value={data.matches || 0}
        color="#FF8C42"
      />
      <StatCard
        icon="📅"
        label="Événements"
        value={data.events || 0}
        color="#4CAF50"
      />
      <StatCard
        icon="⬇️"
        label="Téléchargements"
        value={data.downloads || 0}
        color="#2196F3"
      />
    </>
  );
};

// ============================================================================
// STAT CARD COMPONENT
// ============================================================================
const StatCard = ({ icon, label, value, color }) => {
  return (
    <div className="stat-card" style={{ borderLeftColor: color }}>
      <div className="stat-icon" style={{ color }}>
        {icon}
      </div>
      <div className="stat-content">
        <span className="stat-label">{label}</span>
        <span className="stat-value" style={{ color }}>
          {value}
        </span>
      </div>
    </div>
  );
};

// ============================================================================
// PHOTOGRAPHER QUICK ACTIONS
// ============================================================================
const PhotographerActions = ({ navigate }) => {
  return (
    <>
      <ActionCard
        icon="➕"
        title="Créer un événement"
        description="Lancer un nouvel événement photo"
        onClick={() => navigate('/create-event')}
        color="#4CAF50"
      />
      {/*}
      <ActionCard
        icon="📤"
        title="Importer des photos"
        description="Ajouter des photos à vos événements"
        onClick={() => navigate('/photos/upload')}
        color="#2196F3"
      />
      */}
      <ActionCard
        icon="📊"
        title="Voir les analyses"
        description="Consulter vos indicateurs de performance"
        onClick={() => navigate('/analytics')}
        color="#FF8C42"
      />
      <ActionCard
        icon="⚙️"
        title="Paramètres"
        description="Gérer votre compte"
        onClick={() => navigate('/settings')}
        color="#9C27B0"
      />
    </>
  );
};

// ============================================================================
// USER QUICK ACTIONS
// ============================================================================
const UserActions = ({ navigate, user }) => {
  return (
    <>
      <ActionCard
        icon="🔍"
        title="Trouver mes photos"
        description="Rechercher les photos où vous apparaissez"
        onClick={() => navigate('/my-photos')}
        color="#FF8C42"
      />
      <ActionCard
        icon="📊"
        title="Voir les analyses"
        description="Consulter vos statistiques photo"
        onClick={() => navigate('/analytics')}
        color="#4CAF50"
      />
      {!user.face_encoded && (
        <ActionCard
          icon="📷"
          title="Importer une photo de profil"
          description="Activer la reconnaissance faciale"
          onClick={() => navigate('/profile')}
          color="#E74C3C"
          highlight
        />
      )}
      <ActionCard
        icon="⚙️"
        title="Paramètres"
        description="Gérer votre compte"
        onClick={() => navigate('/settings')}
        color="#9C27B0"
      />
    </>
  );
};

// ============================================================================
// ACTION CARD COMPONENT
// ============================================================================
const ActionCard = ({ icon, title, description, onClick, color, highlight }) => {
  return (
    <div
      className={`action-card ${highlight ? 'highlight' : ''}`}
      onClick={onClick}
      style={{ borderColor: color }}
    >
      <div className="action-icon" style={{ color }}>
        {icon}
      </div>
      <div className="action-content">
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      <div className="action-arrow" style={{ color }}>
        →
      </div>
    </div>
  );
};

// ============================================================================
// PHOTOGRAPHER RECENT ACTIVITY
// ============================================================================
const PhotographerActivity = () => {
  const [recentEvents, setRecentEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentEvents();
  }, []);

  const fetchRecentEvents = async () => {
    try {
      const response = await api.get('/events?limit=5');
      setRecentEvents(response.events || []);
    } catch (error) {
      console.error('Recent events error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="activity-loading">Chargement des événements récents...</div>;
  }

  if (!recentEvents.length) {
    return (
      <div className="empty-state">
        <p>Aucun événement récent. Créez votre premier événement !</p>
      </div>
    );
  }

  return (
    <div className="activity-list">
      {recentEvents.map((event) => (
        <div key={event.id} className="activity-item">
          <div className="activity-icon">📅</div>
          <div className="activity-content">
            <h4>{event.name}</h4>
            <p>{event.category} • {new Date(event.event_date).toLocaleDateString()}</p>
          </div>
          <div className="activity-stats">
            <span>{event.photos_count || 0} photos</span>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// USER RECENT ACTIVITY
// ============================================================================
const UserActivity = () => {
  const [recentMatches, setRecentMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentMatches();
  }, []);

  const fetchRecentMatches = async () => {
    try {
      const response = await api.get('/my-photos?limit=5');
      // Get first 5 photos from all events
      const allPhotos = (response.data || []).flatMap(event => 
        event.photos.map(photo => ({
          ...photo,
          event_name: event.event_name,
          event_category: event.event_category
        }))
      ).slice(0, 5);
      setRecentMatches(allPhotos);
    } catch (error) {
      console.error('Recent matches error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="activity-loading">Chargement des correspondances récentes...</div>;
  }

  if (!recentMatches.length) {
    return (
      <div className="empty-state">
        <p>Aucune photo trouvée pour le moment. Importez une photo de profil pour commencer !</p>
      </div>
    );
  }

  return (
    <div className="activity-list">
      {recentMatches.map((match, index) => (
        <div key={index} className="activity-item">
          <div className="activity-icon">📸</div>
          <div className="activity-content">
            <h4>{match.event_name}</h4>
            <p>{match.event_category} - {match.confidence_score.toFixed(1)}% de correspondance</p>
          </div>
          <div className="activity-badge">
            {match.confidence_score >= 90 ? '🎯' : '✓'}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Dashboard;
